import { useEffect, useMemo } from 'react';
import { orderBy } from 'lodash';
import { DEFAULT_ERROR_MESSAGE } from '@/components/hooks/useToast';
import { isAxiosError } from '@/core/api';
import { CopilotCommunicationResult, CopilotQueryFlowResult, Message } from './useCopilotCommunication.types';
import { useMessagesStore } from './useMessagesStore';

export type { Message };

const stringifyData = (data: unknown): string => {
  if (typeof data === 'object') {
    return JSON.stringify(data);
  } else if (typeof data === 'string') {
    return data;
  }

  return String(data);
};

const stringifyDataError = (error: unknown) => (error ? stringifyData(error) : error);

type MessagesStore = ReturnType<typeof useMessagesStore>;

export interface CopilotCommunicationBridgeProps {
  queryFlow: CopilotQueryFlowResult;
  store: MessagesStore;
}

/**
 * Mode-agnostic bridge that connects a query flow result with a messages store.
 * Triggers the query when a new user message arrives and persists the response.
 */
export const useCopilotCommunication = ({
  queryFlow,
  store,
}: CopilotCommunicationBridgeProps): CopilotCommunicationResult => {
  const { response, citations, dateExtraction, error, subsetPath, responseType, messageId, isFetching, runQuery } =
    queryFlow;
  const { userMessages, copilotMessages, lastUserMessage, addUserMessage, addCopilotMessage } = store;

  const messages = useMemo(() => {
    return orderBy([...userMessages.values(), ...copilotMessages.values()], (item) => item.time, 'asc');
  }, [userMessages, copilotMessages]);

  useEffect(() => {
    runQuery();
  }, [lastUserMessage]);

  useEffect(() => {
    const handleError = (err: unknown) => {
      const errorText = isAxiosError(err)
        ? stringifyDataError(err.response?.data.details) || err.response?.data.message || DEFAULT_ERROR_MESSAGE
        : (err && (err as Error).message) || DEFAULT_ERROR_MESSAGE;

      addCopilotMessage({
        message: errorText,
        messageId: `error-${messageId}`,
        responseType: 'client_error',
      });
    };

    if (error) {
      handleError(error);
      return;
    }

    if (response || dateExtraction) {
      addCopilotMessage({
        message: stringifyData(response),
        messageId,
        subsetPath,
        citations,
        dateExtraction,
        responseType,
      });
      return;
    }
  }, [response, citations, dateExtraction, error, subsetPath, responseType, messageId]);

  return {
    messages,
    isFetching,
    addUserMessage,
  };
};
